/** The caller's approval inbox across modules: what awaits them, with approve / reject / return. */
import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useQuery, invalidate } from '../lib/hooks.ts';
import { useI18n } from '../lib/i18n.tsx';
import { useMeta } from '../lib/meta.tsx';
import { Link } from '../lib/router.tsx';
import { Button, Card, Dialog, EmptyState, ErrorState, Field, Spinner, useToast } from '../components/ui.tsx';
import { PageHead } from '../components/PageHead.tsx';
import { ApprovalSteps } from './RecordPage.tsx';
import type { Row } from '../lib/types.ts';

export function ApprovalsPage() {
  const { t, dateTime } = useI18n();
  const { entities } = useMeta();
  const toast = useToast();
  const { data, error, loading, refresh } = useQuery('approvals', () => api.get<Row[]>('approvals'), { ttlMs: 0 });
  const [target, setTarget] = useState<{ row: Row; decision: 'approved' | 'rejected' | 'returned' } | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const decide = async () => {
    if (!target) return;
    if (target.decision !== 'approved' && !comment.trim()) { toast.error(t('workflow.commentRequired')); return; }
    setBusy(true);
    try {
      await api.post(`approvals/${target.row.id}/decide`, { decision: target.decision, comment: comment.trim() || undefined });
      toast.success(t('common.saved')); setTarget(null); setComment('');
      invalidate('dash:home'); invalidate('list:'); await refresh();
    } catch (e) { toast.error(e); } finally { setBusy(false); }
  };
  return (
    <div className="stack">
      <PageHead title={t('workflow.inbox')} sub={data ? `${data.length}` : undefined} />
      {error ? <ErrorState error={error} retry={() => void refresh()} /> : loading && !data ? <Spinner /> : !data || data.length === 0 ? <Card><EmptyState title={t('workflow.nothingToApprove')} icon="ShieldCheck" /></Card> : data.map((r) => {
        const def = entities.get(String(r.entity));
        const steps = (r.steps as Row[]) ?? [];
        const current = steps.find((s) => Number(s.step) === Number(r.current_step));
        return (
          <Card key={String(r.id)} title={<span className="row" style={{ gap: 10 }}>{def && <Link to={`${def.route}/${r.record_id}`}>{String(r.record_number ?? def.label)}</Link>}<span>{String(r.title)}</span></span>} actions={<span className="subtle">{t('workflow.requestedBy')} {String(r.requested_by_name ?? '')} · {dateTime(r.requested_at)}</span>}>
            <div className="row" style={{ alignItems: 'flex-start' }}>
              <div className="grow"><div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>{t('workflow.step')} {String(r.current_step)}: {String(current?.label ?? '')}</div><ApprovalSteps steps={steps} /></div>
              <div className="actions-row">
                <Button variant="primary" icon="Check" onClick={() => setTarget({ row: r, decision: 'approved' })}>{t('workflow.approve')}</Button>
                <Button icon="Undo2" onClick={() => setTarget({ row: r, decision: 'returned' })}>{t('workflow.return')}</Button>
                <Button variant="destructive" icon="X" onClick={() => setTarget({ row: r, decision: 'rejected' })}>{t('workflow.reject')}</Button>
              </div>
            </div>
          </Card>
        );
      })}
      <Dialog open={!!target} onClose={() => setTarget(null)} title={target ? `${t(`workflow.${target.decision === 'approved' ? 'approve' : target.decision === 'rejected' ? 'reject' : 'return'}`)}: ${String(target.row.title)}` : ''}
        footer={<><Button onClick={() => setTarget(null)}>{t('common.cancel')}</Button><Button variant={target?.decision === 'rejected' ? 'destructive' : 'primary'} busy={busy} onClick={() => void decide()}>{t('common.confirm')}</Button></>}>
        <Field label={t('common.comment')} required={target?.decision !== 'approved'}><textarea className="input" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} autoFocus /></Field>
      </Dialog>
    </div>
  );
}
